/**
 * Shared CustomFieldModal Component
 *
 * Reusable modal for creating and managing custom field mappings across survey types.
 * Used by: CustomSurvey only
 *
 * Features:
 * - Select custom field from dropdown
 * - Filter available custom fields by field type
 * - Disable already used custom fields
 * - Keyboard shortcuts (Enter to save, Escape to cancel)
 * - Consistent modal styling
 */

import { FormFieldType, IFormField } from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import React, { useMemo, useState } from 'react';
import { Button } from '../../../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../../../components-v2/Dropdown';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { Modal } from '../../../../../components/Modal';

/**
 * Props for CustomFieldModal component
 */
interface CustomFieldModalProps {
	isOpen: boolean;
	onRequestClose: (
		result?: { fieldId: string; customFieldId: string; customFieldName: string } | null,
		canceled?: boolean
	) => void;
	questionLabel: string;
	fieldId: string;
	hasExistingMapping?: boolean;
	availableCustomFields?: IFormField<string>[];
	usedCustomFieldIds?: string[];
	selectedCustomField?: {
		id: string;
		name: string;
		label: string;
	};
	className?: string;
}

/**
 * Reusable CustomFieldModal component
 */
export const CustomFieldModal: React.FC<CustomFieldModalProps> = ({
	isOpen,
	onRequestClose,
	questionLabel,
	fieldId,
	hasExistingMapping = false,
	availableCustomFields = [],
	usedCustomFieldIds = [],
	selectedCustomField,
	className,
}) => {
	const [selectedCustomFieldId, setSelectedCustomFieldId] = useState(
		selectedCustomField ? selectedCustomField.label || selectedCustomField.name || selectedCustomField.id || '' : ''
	);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	// Reset the selection when modal opens
	React.useEffect(() => {
		if (isOpen) {
			const initialId = selectedCustomField
				? selectedCustomField.label || selectedCustomField.name || selectedCustomField.id || ''
				: '';
			setSelectedCustomFieldId(initialId);
			setIsDropdownOpen(false);
		} else {
			// Reset when modal closes
			setSelectedCustomFieldId('');
			setIsDropdownOpen(false);
		}
	}, [isOpen, selectedCustomField, hasExistingMapping]);

	// Filter available custom fields to only include String and Option types
	const filteredCustomFields = useMemo(() => {
		return availableCustomFields.filter(
			field => field.fieldType === FormFieldType.String || field.fieldType === FormFieldType.Option
		);
	}, [availableCustomFields]);

	// Create options for the select dropdown
	const customFieldOptions = useMemo(() => {
		return filteredCustomFields.map(field => {
			// Use same prioritization as customFieldName to ensure consistent matching
			const optionId = field.name || field.id || '';
			return {
				id: optionId,
				label: field.label || 'Unnamed Field',
				disabled: usedCustomFieldIds.includes(optionId),
			};
		});
	}, [filteredCustomFields, usedCustomFieldIds]);

	// Handle save
	const handleSave = () => {
		if (!selectedCustomFieldId) {
			return;
		}

		// Try to find the field by id, name, or label
		const selectedField = filteredCustomFields.find(
			field =>
				field.id === selectedCustomFieldId ||
				field.name === selectedCustomFieldId ||
				field.label === selectedCustomFieldId
		);

		if (!selectedField) {
			return;
		}

		onRequestClose({
			fieldId,
			customFieldId: selectedField.id || selectedField.name || '',
			customFieldName: selectedField.name || 'Unnamed Field',
		});
	};

	// Handle cancel
	const handleCancel = () => {
		setSelectedCustomFieldId(''); // Reset selection
		onRequestClose(null, true);
	};

	// Handle keyboard shortcuts
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSave();
		} else if (e.key === 'Escape') {
			e.preventDefault();
			handleCancel();
		}
	};

	const modalTitle = hasExistingMapping ? 'Edit Custom Field Mapping' : 'Map to Custom Field';
	const saveButtonText = hasExistingMapping ? 'Update' : 'Save';

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleCancel}
			title={modalTitle}
			className={className}
			onKeyDown={handleKeyDown}
			useStyledHeader
			shouldCloseOnOverlayClick
		>
			<div className={css(styles.modalContent)}>
				<h3 className={css(styles.modalTitle)}>Link answers to a custom field</h3>
				<p className={css(styles.modalDescription)}>
					Answers will be added to the contact profile of respondents in the selected custom field.
				</p>
				<p className={css(styles.modalDescription)}>Additional custom fields can be added through Admin.</p>
				<div className={css(styles.fieldGroup)}>
					<label className={css(styles.label)}>
						Q: <span className={css(styles.questionLabel)}>{questionLabel}</span>
					</label>
				</div>

				<div className={css(styles.fieldGroup)}>
					<label className={css(styles.label)}>
						Custom Field <span className={css(styles.required)}>*</span>
					</label>
					<Dropdown open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
						<DropdownSelectTrigger styles={[styles.select]}>
							{customFieldOptions.find(option => option.id === selectedCustomFieldId)?.label || 'Select Custom Field'}
						</DropdownSelectTrigger>
						<DropdownContent styles={[styles.dropdownContent]} align='start'>
							{customFieldOptions.map((option, index) => {
								return (
									<DropdownItem
										key={option.id || option.label || `option-${index}`}
										onClick={() => {
											setSelectedCustomFieldId(option.id);
											setIsDropdownOpen(false); // Close dropdown after selection
										}}
										disabled={option.disabled}
									>
										{option.label}
									</DropdownItem>
								);
							})}
						</DropdownContent>
					</Dropdown>
					{filteredCustomFields.length === 0 && (
						<div className={css(styles.noFieldsMessage)}>
							No custom fields available. Only String and Option field types are supported.
						</div>
					)}
				</div>

				<div className={css(styles.buttonGroup)}>
					<Button
						type='button'
						onClick={handleSave}
						variant='primary'
						disabled={!selectedCustomFieldId}
						styles={[styles.saveButton]}
						title={`Save button disabled: ${!selectedCustomFieldId ? 'No field selected' : 'Ready to save'}`}
					>
						{saveButtonText}
					</Button>
					<Button type='button' onClick={handleCancel} styles={[styles.cancelButton]} variant='secondary'>
						Cancel
					</Button>
				</div>
			</div>
		</Modal>
	);
};

const { spacing, colors, common, typography } = THEME;

const styles = StyleSheet.create({
	modalContent: {
		width: '100%',
		maxWidth: '400px',
	},
	fieldGroup: {
		marginBottom: `${spacing.md}px`,
	},
	label: {
		display: 'block',
		marginBottom: `${spacing.xs}px`,
		fontWeight: 'bold',
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
	},
	questionLabel: {
		fontWeight: 'normal',
		color: colors.text.light,
		fontStyle: 'italic',
	},
	required: {
		color: colors.danger.main,
	},
	select: {
		width: '100%',
	},
	noFieldsMessage: {
		marginTop: '8px',
		padding: `${spacing.sm}px`,
		backgroundColor: colors.background.light,
		border: common.border,
		borderRadius: common.borderRadius,
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		textAlign: 'center',
	},
	buttonGroup: {
		display: 'flex',
		gap: `${spacing.sm}px`,
		marginTop: `${spacing.md}px`,
	},
	modalTitle: {
		fontSize: typography.fontSizes.lg,
		fontWeight: 'bold',
		marginBottom: `${spacing.sm}px`,
	},
	modalDescription: {
		fontSize: typography.fontSizes.sm,
		marginBottom: `${spacing.sm}px`,
		color: colors.text.light,
	},
	dropdownContent: {
		width: '100%',
		maxHeight: 300,
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	dropdownItem: {
		width: '100%',
		minWidth: 200,
	},
	saveButton: {
		width: 150,
	},
	cancelButton: {
		width: 94,
	},
});
